import React from "react";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import TriviaQuestionStage from "../TriviaQuestionStage";
import TriviaPlayerOptionCard from "../TriviaPlayerOptionCard";
import { ARCADE_ACCENT, getTriviaFormatLabel } from "../../../utils/triviaArcade";

const answerLabel = (question) => {
  const answer = question?.correctAnswer;
  if (answer == null) return null;
  if (typeof answer === "boolean") return answer ? "True" : "False";
  return answer.displayName || answer.text || String(answer.playerId ?? answer.value);
};

const TriviaPreviewPane = ({
  sport = "mlb",
  questions = [],
  selectedQuestion,
  selectedIndex,
  onSelectQuestion,
  isLoading,
  error,
  isEmptyPack,
  presetVersion,
  resampleCount,
}) => (
  <Box>
    <Stack
      direction="row"
      spacing={1}
      sx={{ flexWrap: "wrap", rowGap: 1, mb: 2 }}
    >
      <Chip size="small" label={`${questions.length} questions`} />
      {presetVersion != null && (
        <Chip size="small" variant="outlined" label={`Preset v${presetVersion}`} />
      )}
      {resampleCount != null && (
        <Chip
          size="small"
          variant="outlined"
          label={`${resampleCount} resamples`}
        />
      )}
    </Stack>

    {isLoading && (
      <LinearProgress
        sx={{
          mb: 2,
          "& .MuiLinearProgress-bar": { backgroundColor: ARCADE_ACCENT },
        }}
      />
    )}

    {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )}

    {!error && isEmptyPack && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        The engine returned an empty pack. Widen the preset windows or pick a
        different category.
      </Alert>
    )}

    {questions.length > 0 && (
      <List
        dense
        disablePadding
        sx={{
          mb: 3,
          maxHeight: 260,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {questions.map((question, index) => (
          <ListItemButton
            key={question.inspector?.fingerprint || `${question.question}-${index}`}
            aria-label={`Preview question ${index + 1} ${question.question}`}
            selected={selectedIndex === index}
            onClick={() => onSelectQuestion(index)}
            sx={{
              borderLeft: 3,
              borderColor:
                selectedIndex === index ? ARCADE_ACCENT : "transparent",
            }}
          >
            <ListItemText
              primary={question.question}
              secondary={`Q${index + 1} · ${getTriviaFormatLabel(question.type)}`}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            />
          </ListItemButton>
        ))}
      </List>
    )}

    {selectedQuestion ? (
      <Box>
        <TriviaQuestionStage question={selectedQuestion} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {(selectedQuestion.options || []).map((option, index) => (
            <TriviaPlayerOptionCard
              key={option.playerId || option.value || option.text || index}
              option={option}
              sport={sport}
              questionSubcategory={selectedQuestion.subcategory}
              disabled
            />
          ))}
        </Box>
        {answerLabel(selectedQuestion) && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {`Answer: ${answerLabel(selectedQuestion)}`}
          </Typography>
        )}
      </Box>
    ) : (
      !isLoading &&
      !error && (
        <Typography color="text.secondary">
          No pack yet. Set your options above and hit Regenerate.
        </Typography>
      )
    )}
  </Box>
);

export default TriviaPreviewPane;
